//TODO: when time allows, refactor this component to use react-day-picker latest version
import DatePickerInput from "react-day-picker/DayPickerInput";
import { map } from "lodash";
import { useCallback, useState, useMemo } from "react";
import { WrappedFieldInputProps } from "redux-form";
import moment from "moment";

import {
  CALENDAR_WEEKDAYS,
  MAX_SEARCHABLE_MONTHS,
  MONTHS_SHORT,
  TODAY,
  TOMORROW,
} from "src/constants";
import { translate } from "src/services";
import { HorizontalAlign, VerticalAlign } from "src/styled/DatePicker";
import {
  DatePicker as DatePickerContainer,
  FormError,
  FormGroup,
  FormGroupClear,
  FormLabel,
  FormMeta,
} from "../styled";

export const CALENDAR_INPUT_LENGTH = 10;

interface Props {
  color?: string;
  customMaxFutureSearchableMonths?: number;
  customProp?: any;
  disabled?: boolean;
  disabledDays?: any[];
  disableFormSubmitOnDateChange?: boolean;
  horizontalAlign?: HorizontalAlign;
  id?: string;
  input: WrappedFieldInputProps;
  inputDefaultValue?: Date | string;
  isClearable?: boolean;
  isTodayWithNoStyle?: boolean;
  label?: string;
  margin?: string;
  mask?: string;
  meta: any;
  metaInfo?: string;
  name?: string;
  noBottomBorder?: boolean;
  onDateChange?: (date: Date | string) => void;
  placeholder?: string;
  raisedLabel?: boolean;
  tabletAlignHalfLeft?: boolean;
  tabletAlignLeft?: boolean;
  verticalAlign?: VerticalAlign;
  width?: string;
}

const DatePicker = ({
  color,
  customMaxFutureSearchableMonths,
  disabled,
  disabledDays = [],
  disableFormSubmitOnDateChange,
  horizontalAlign,
  id,
  input,
  inputDefaultValue,
  isClearable,
  isTodayWithNoStyle,
  label,
  margin,
  mask,
  meta: { submitFailed, error },
  metaInfo,
  name,
  noBottomBorder,
  onDateChange,
  placeholder,
  tabletAlignHalfLeft,
  tabletAlignLeft,
  verticalAlign,
  width,
  ...props
}: Props) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(moment(input.value).toDate());
  const [hasChangedValue, setHasChangedValue] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(
    moment(input.value).isValid() ? moment(input.value).format("MM/DD/YYYY") : input.value
  );
  const [isDateValid, setIsDateValid] = useState<boolean>(true);
  const [isLabelRaised, setIsLabelRaised] = useState<boolean>(input.value.length || !!placeholder);
  const [isInputChangedByTyping, setIsInputChangedByTyping] = useState<boolean>(false);

  const [inputValueLength, setInputValueLength] = useState<number>(inputValue.length);
  const maxSearchableMonths = MAX_SEARCHABLE_MONTHS;

  const maxFutureSearchableMonths =
    !!customMaxFutureSearchableMonths || customMaxFutureSearchableMonths === 0
      ? customMaxFutureSearchableMonths
      : maxSearchableMonths;

  const allDisabledDays = useMemo(
    () => [
      ...(disabledDays || []),
      disabledDays && disabledDays.length > 0 && disabledDays[0].before
        ? {
            before: disabledDays[0].before,
            after: moment().add(maxFutureSearchableMonths, "months").toDate(),
          }
        : {
            before: moment().subtract(maxSearchableMonths, "months").toDate(),
            after: moment().add(maxFutureSearchableMonths, "months").toDate(),
          },
    ],
    [disabledDays, maxFutureSearchableMonths, maxSearchableMonths]
  );

  const onDayChange = useCallback(
    (date: Date | string | Object, _: any, calendarInput?: any) => {
      setInputValueLength(calendarInput?.getInput().value.length);
      calendarInput?.getInput().value.length === 0 && setIsLabelRaised(!!placeholder);
      if (
        date &&
        moment(date).format("MM/DD/YYYY").length === CALENDAR_INPUT_LENGTH &&
        calendarInput?.getInput().value.length === CALENDAR_INPUT_LENGTH
      ) {
        const newValue = moment(date).format("MM/DD/YYYY");
        input.onChange(newValue);
        setInputValue(newValue);
        setHasChangedValue(true);
        setIsDateValid(true);
        if (typeof onDateChange === "function") {
          onDateChange(newValue);
        }
      } else if (calendarInput?.getInput().value.length === 0) {
        input.onChange(null);
        setInputValue("");
        setHasChangedValue(true);
        setIsDateValid(!!placeholder);
        setIsDateValid(false);
        setInputValueLength(0);
      }
    },
    [input, onDateChange, placeholder]
  );

  const onBlur = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isInputChangedByTyping) {
        const value = (event.target as any).value;
        const currentDate = TODAY;

        let isDateValid = moment(value || inputValue, "MM/DD/YYYY", true).isValid();
        if (value.length === 0 && inputValue.toString().length === 0) isDateValid = true;
        setIsDateValid(isDateValid);

        let isAfterDisabled = false;
        let isBeforeDisabled = false;
        let isDayDisabled = false;
        let isDayOfWeekDisabled = false;
        let isBlackoutOrSeasonalDayDisabled = false;

        if (value && isDateValid) {
          console.log(allDisabledDays);
          allDisabledDays.forEach((disabledDay) => {
            if (
              (disabledDay.after &&
                moment(moment(disabledDay.after).format("MM/DD/YYYY")).diff(moment(value), "days") <
                  0) ||
              Math.abs(moment(currentDate).diff(moment(value), "months")) >
                maxFutureSearchableMonths
            ) {
              isAfterDisabled = true;
            }

            if (
              (disabledDay.before && moment(value).diff(moment(disabledDay.before), "days") < 0) ||
              moment(value).diff(moment().subtract(maxFutureSearchableMonths, "months").toDate()) <
                0
            ) {
              isBeforeDisabled = true;
            }

            if (moment(disabledDay).format("MM/DD/YYYY") === moment(value).format("MM/DD/YYYY")) {
              isDayDisabled = true;
            }

            if (disabledDay.daysOfWeek?.includes(moment(value).day())) {
              isDayOfWeekDisabled = true;
            }

            if (typeof disabledDay === "function" && !!disabledDay(value)) {
              isBlackoutOrSeasonalDayDisabled = true;
            }
          });

          if (
            isDateValid &&
            (isAfterDisabled ||
              isBeforeDisabled ||
              isDayDisabled ||
              isDayOfWeekDisabled ||
              isBlackoutOrSeasonalDayDisabled) &&
            hasChangedValue
          ) {
            setInputValue("");
            setHasChangedValue(true);

            input.onChange("");
          }

          if (!isDateValid && !disableFormSubmitOnDateChange) {
            setInputValue(value);
            setHasChangedValue(true);
            input.onChange(value);
          }

          if (
            value &&
            ((!moment(input.value).isValid() && !moment(inputValue).isValid()) ||
              (inputValueLength && inputValueLength > 10) ||
              (inputValueLength === 0 && input.value.length === 0) ||
              input.value.length > 10)
          ) {
            setInputValue(moment().format("MM/DD/YYYY"));
            setInputValueLength(10);
            setIsDateValid(true);
            setHasChangedValue(true);

            input.onChange(moment().format("MM/DD/YYYY"));
          }
        }
      }

      setIsInputChangedByTyping(false);
    },
    [
      allDisabledDays,
      disableFormSubmitOnDateChange,
      hasChangedValue,
      input,
      inputValue,
      inputValueLength,
      isInputChangedByTyping,
      maxFutureSearchableMonths,
    ]
  );

  const clearInput = useCallback(() => {
    const currentMonth = new Date(moment().year(), moment().month());
    setInputValue("");
    setCurrentMonth(currentMonth);
    setIsLabelRaised(!!placeholder);
    setInputValueLength(0);
    setIsDateValid(false);
    setHasChangedValue(true);
    input.onChange(null);
  }, [input, placeholder]);

  const parseDate = (str: string, format: string) => {
    const date = moment(str, format, true);
    return date.isValid() ? date.toDate() : undefined;
  };

  const formatDate = (date: Date | string, format: string) => moment(date).format(format);

  const onInputChange = useCallback(
    (ev: any) => {
      setIsInputChangedByTyping(true);

      const str = ev.target.value;
      const value = input.value;

      str.length > 0 ? setIsLabelRaised(true) : str.length === 0 && setIsLabelRaised(!!placeholder);
      if (str.length !== CALENDAR_INPUT_LENGTH) return;

      const currentDate = TODAY;
      const nextDay = TOMORROW;

      const isAfter =
        disabledDays && disabledDays[0].after
          ? moment(disabledDays[0].after).diff(moment(str), "days") < 0
          : Math.abs(moment(currentDate).diff(moment(str), "months")) > maxFutureSearchableMonths;
      const isBefore =
        disabledDays && disabledDays[0].before
          ? moment(str).diff(moment(disabledDays[0].before), "days") < 0
          : moment(str).diff(moment().subtract(maxFutureSearchableMonths, "months").toDate()) < 0;

      const resetToDay =
        disabledDays &&
        disabledDays[0].before !== currentDate &&
        moment(disabledDays[0].after).diff(moment(str), "days") > -2
          ? nextDay
          : currentDate;
      const newValue = (isAfter || isBefore) && typeof value === "string" ? resetToDay : str;
      const date = moment(newValue);

      input.onChange(date.format("MM/DD/YYYY"));
      setInputValue(date.format("MM/DD/YYYY"));
      setHasChangedValue(true);

      if (!date.isValid()) {
        setInputValue("");
        setIsLabelRaised(!!placeholder);
        setHasChangedValue(true);
        input.onChange(null);
      }
    },
    [disabledDays, input, maxFutureSearchableMonths, placeholder]
  );

  const onCalendarInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    const supportedCharacters = [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "ArrowLeft",
      "ArrowRight",
      "Backspace",
      "/",
    ];

    if (!supportedCharacters.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const hasTodayButton = false;

  const months = map(MONTHS_SHORT, (month) => translate(month.translationKey));
  const weekdaysShort = map(CALENDAR_WEEKDAYS, (day) => translate(day.translationKey));
  const currentMonthToShow = new Date(moment().year(), moment().month());

  const valueToShow =
    input.value && !inputValue ? input.value : input.value && inputValue ? inputValue : "";

  return (
    <FormGroup
      hasValue={!!valueToShow}
      margin={margin}
      raisedLabel={isLabelRaised}
      width={width}
      disabled={disabled}
    >
      {!!label && <FormLabel>{label}</FormLabel>}

      <DatePickerContainer
        focus={true}
        horizontalAlign={horizontalAlign}
        isTodayWithNoStyle={isTodayWithNoStyle}
        mask={!!valueToShow && !!mask ? formatDate(valueToShow, mask) : undefined}
        position="relative"
        tabletAlignHalfLeft={tabletAlignHalfLeft}
        tabletAlignLeft={tabletAlignLeft}
        verticalAlign={verticalAlign}
        color={color}
        noBottomBorder={noBottomBorder}
      >
        <DatePickerInput
          onDayChange={onDayChange}
          parseDate={parseDate}
          formatDate={formatDate}
          value={
            inputDefaultValue && moment(inputDefaultValue).isValid()
              ? inputDefaultValue
              : moment(valueToShow).isValid()
              ? valueToShow
              : isClearable
              ? ""
              : ""
          }
          keepFocus
          placeholder={placeholder || ""}
          inputProps={{
            disabled,
            name,
            id,
            readOnly: false,
            autoComplete: "off",
            onChange: onInputChange,
            onKeyDown: onCalendarInputKeyDown,
            onBlur: onBlur,
          }}
          dayPickerProps={{
            month: moment(currentMonth).isValid()
              ? (currentMonth as Date | undefined)
              : (currentMonthToShow as Date | undefined),
            disabledDays: allDisabledDays,
            months,
            weekdaysShort,
            todayButton: hasTodayButton ? translate("common.today") : undefined,
          }}
        />

        {isClearable && !!valueToShow && (
          <FormGroupClear disabled={disabled} onClick={clearInput} />
        )}
      </DatePickerContainer>

      {!!metaInfo && <FormMeta align="right">{metaInfo}</FormMeta>}

      {((submitFailed && error) || !isDateValid) && (
        <FormError align="right">
          {error || translate("common.validationMessages.invalidDate")}
        </FormError>
      )}
    </FormGroup>
  );
};

export default DatePicker;
